import { useEffect, useRef } from 'react';

/**
 * Runs `refresh` whenever the tab or panel comes back to the foreground.
 *
 * A panel left open in the background misses anything that happened meanwhile — a colleague's sync,
 * the nightly job. Reloading on the way back keeps what is on screen consistent with the server.
 *
 * The callback is held in a ref so an inline arrow function does not re-subscribe on every render.
 */
export const useRefreshOnVisible = (refresh: () => void): void => {
    const refreshRef = useRef(refresh);

    // In an effect rather than during render, matching useRefreshOnPortalMessage: a render can be thrown
    // away or replayed, and mutating a ref on that path is not something React guarantees.
    useEffect(() => {
        refreshRef.current = refresh;
    }, [refresh]);

    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') refreshRef.current();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, []);
};
